import type { FieldContent, OcrExtractionMode } from "@capture/shared";
import {
  useFormConfigStore,
  resolveV3,
  activeForm as resolveActiveForm,
} from "../form-config/state/store";

// ═══════════════════════════════════════════════════════════════
// OcrProfilesPanel — per-form, store-bound OCR field-subset editor
//
// OCR Caller-Driven Extraction. The operator picks which of the ACTIVE form's
// registry fields OCR extracts, sets the per-form extraction mode (Zoned /
// Full-page), and manages per-field label aliases + relation hints. The
// primitive type is shown read-only from the registry `dataType` — never
// selected.
//
// Task 5 — this panel is now per-form. It reads the ACTIVE (prop) form's fields
// + `ocrExtractionMode` straight from the form-config store (the same source
// FieldListPane/ZonesTab use) and writes back via the store's id-scoped actions
// (`updateForm` / `updateFormFields`). Writes target the RESOLVED form's id —
// never the store's `activeFormId`, which can diverge from this prop because the
// draft store is a singleton that persists `selectForm` across surfaces. There
// is no Save button or load/error state — the store is the single source of
// truth and persists through the same draft autosave as every other section.
// ═══════════════════════════════════════════════════════════════

/** Operator-facing label for each relation. Empty value = server default. */
const RELATION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "", label: "Auto (right or below)" },
  { value: "right", label: "Right — same line, requires separator" },
  { value: "below", label: "Below — value on the next line" },
  { value: "above", label: "Above — value on the prior line" },
  { value: "same-line-colon", label: "Same line, colon required (legacy)" },
  { value: "between-lines", label: "Between two labels" },
];

/**
 * Render a field's per-field confidence floor as a percentage. Returns
 * `"default"` (rendered muted) when the field inherits the JT-level floor. The
 * editor for this value lives in the field drawer's ValidationSection (R-128);
 * this panel only displays the current value.
 */
function formatFloorHint(verifyThreshold: number | null | undefined): {
  text: string;
  muted: boolean;
} {
  if (verifyThreshold === null || verifyThreshold === undefined) {
    return { text: "default", muted: true };
  }
  return { text: `${Math.round(verifyThreshold * 100)}%`, muted: false };
}

/** Map a registry `dataType` to the displayed OCR primitive type. */
function primitiveTypeOf(dataType: string): string {
  switch (dataType) {
    case "number":
    case "integer":
    case "decimal":
      return "number";
    case "date":
      return "date";
    case "currency":
      return "currency";
    case "boolean":
      return "boolean";
    default:
      return "text";
  }
}

interface OcrProfilesPanelProps {
  /**
   * The form currently in scope. `null` resolves to the default form via the
   * same fallback the store's `activeForm` helper uses. Per-field + per-form OCR
   * edits are scoped to it.
   */
  activeFormId: string | null;
}

export function OcrProfilesPanel({ activeFormId }: OcrProfilesPanelProps) {
  // contentV3 is the source of truth; `content` is the derived legacy view.
  // resolveV3 migrates a legacy-only seed on the fly (test/pre-load paths).
  // The active form falls back to the default form when activeFormId is
  // null/stale — the same fallback the store's mutating actions use, so the read
  // path and write path can never drift onto different forms.
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const legacyContent = useFormConfigStore((s) => s.content);
  // Both writes are id-scoped to the RESOLVED form's id (see header note):
  // never the store's `activeFormId`, which can lag behind this prop.
  const updateForm = useFormConfigStore((s) => s.updateForm);
  const updateFormFields = useFormConfigStore((s) => s.updateFormFields);

  const v3 = resolveV3({ contentV3, content: legacyContent });
  const form = v3 ? resolveActiveForm(v3, activeFormId) : null;

  if (!form) {
    return (
      <div className="panel-content">
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">No form configuration loaded.</span>
        </div>
      </div>
    );
  }

  const fields = form.fields;
  // F-072: an UNSET mode must never render as an active "Full-page" config —
  // that misled a real operator into believing extraction was on. Unset stays
  // unset (no persisted "none" value) until the operator picks a real mode.
  const mode: OcrExtractionMode | null = form.ocrExtractionMode ?? null;

  /** Map one field on the resolved form and write the whole list back (id-scoped). */
  const patchField = (fieldId: string, patch: (f: FieldContent) => FieldContent) => {
    const nextFields = form.fields.map((f) =>
      f.fieldDefinitionId === fieldId ? patch(f) : f,
    );
    updateFormFields(form.id, nextFields);
  };

  const setMode = (next: OcrExtractionMode) => {
    updateForm(form.id, { ocrExtractionMode: next });
  };

  const toggleExtract = (fieldId: string, next: boolean) => {
    patchField(fieldId, (f) => {
      // R-124 (2026-05-23) — when OCR is toggled on for a field with no
      // aliases, seed the aliases list with the field's display label. The
      // on-form label almost always matches the field label, so the operator
      // skips the retype-then-tab loop. Toggling off does NOT clear aliases —
      // data is preserved for a re-toggle so any operator-curated set survives.
      if (next && (f.ocrAliases?.length ?? 0) === 0 && f.label) {
        return { ...f, ocrExtracted: true, ocrAliases: [f.label] };
      }
      return { ...f, ocrExtracted: next };
    });
  };

  const updateAliases = (fieldId: string, raw: string) => {
    const aliases = raw
      .split(",")
      .map((a) => a.trim())
      .filter((a) => a.length > 0);
    patchField(fieldId, (f) => ({ ...f, ocrAliases: aliases }));
  };

  const updateRelation = (fieldId: string, relation: string) => {
    // Empty string collapses to null so the OCR server default applies.
    patchField(fieldId, (f) => ({ ...f, ocrAliasRelation: relation ? relation : null }));
  };

  const selectedCount = fields.filter((f) => f.ocrExtracted).length;

  return (
    <div className="panel-content">
      <div className="export-tab-header">
        <h3 className="rb-section-title">OCR Profile</h3>
      </div>

      <p className="rb-muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Select which registry fields the OCR engine extracts for this form. Field
        types are taken from the registry — not chosen here.
      </p>

      <div className="rb-row" style={{ marginBottom: 16 }}>
        <label className="rb-label" htmlFor="ocr-extraction-mode">
          Extraction Mode
        </label>
        <select
          id="ocr-extraction-mode"
          className="rb-select"
          value={mode ?? ""}
          onChange={(e) => {
            // Guard the placeholder — only real modes ever reach the store.
            if (e.target.value) setMode(e.target.value as OcrExtractionMode);
          }}
        >
          {mode === null && (
            <option value="" disabled>
              No extraction configured — select a mode
            </option>
          )}
          <option value="zoned">Zoned (authoritative boxes)</option>
          <option value="fullpage">Full-page (label discovery)</option>
        </select>
      </div>

      {mode === null && (
        <p
          className="rb-muted"
          style={{ fontSize: 12, marginTop: -8, marginBottom: 16, color: "var(--color-warning)" }}
        >
          OCR extraction will NOT run for this form until a mode is chosen.
        </p>
      )}

      {fields.length === 0 ? (
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">
            This form has no fields yet. Define fields first.
          </span>
        </div>
      ) : (
        /* F-084 — real table layout for the per-field list: aligned columns,
           DESIGN.md §5.3 header treatment. Styles live in admin.css under
           .ocr-profile-table (scoped — the bare .rb-table has no base styles). */
        <table className="rb-table ocr-profile-table">
          <thead>
            <tr>
              <th className="ocr-col-toggle">OCR</th>
              <th>Field</th>
              <th className="ocr-col-type">Type</th>
              {/* Read-only confidence-floor hint. Edit in the field drawer's
                  ValidationSection. */}
              <th
                className="ocr-col-floor"
                title="Per-field confidence floor (edit in field drawer → Validation)"
              >
                Floor
              </th>
              <th>Label Aliases</th>
              <th className="ocr-col-relation">Relation</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f) => {
              const id = f.fieldDefinitionId;
              const extracted = f.ocrExtracted ?? false;
              const aliases = f.ocrAliases ?? [];
              const hint = formatFloorHint(f.verifyThreshold);
              return (
                <tr key={id}>
                  <td className="ocr-col-toggle">
                    <input
                      type="checkbox"
                      aria-label={`Extract ${f.label} with OCR`}
                      checked={extracted}
                      onChange={(e) => toggleExtract(id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <span>{f.label}</span>
                    <code className="rb-muted ocr-field-code">
                      {f.internalName ?? f.name}
                    </code>
                  </td>
                  <td>
                    <span className="jt-badge">{primitiveTypeOf(f.dataType)}</span>
                  </td>
                  <td data-testid={`ocr-floor-hint-${id}`}>
                    <span className={hint.muted ? "rb-muted" : undefined}>
                      {hint.text}
                    </span>
                  </td>
                  <td>
                    <input
                      className="rb-input"
                      value={aliases.join(", ")}
                      // R-124 — hint text lives on `placeholder` (vanishes on
                      // focus / typing), never as a pre-populated value.
                      placeholder="comma-separated label variants"
                      disabled={!extracted}
                      onChange={(e) => updateAliases(id, e.target.value)}
                      // R-124 — select-all-on-focus so paste always overwrites.
                      onFocus={(e) => e.currentTarget.select()}
                    />
                  </td>
                  <td>
                    <select
                      className="rb-select"
                      aria-label={`Label relation for ${f.label}`}
                      value={f.ocrAliasRelation ?? ""}
                      disabled={!extracted}
                      onChange={(e) => updateRelation(id, e.target.value)}
                    >
                      {RELATION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="rb-muted" style={{ fontSize: 12, marginTop: 12 }}>
        {selectedCount} of {fields.length} fields selected for OCR extraction.
      </div>
    </div>
  );
}
